/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */
//'use strict';
//import enumerateStats from './util';
const util = require('./util.js');
const enxConfig = require('./../../Common.config.js');
function Call(config, test) {
  this.test = test;
  this.statsGatheringRunning = false;

  this.pc1 = new RTCPeerConnection(config);
  this.pc2 = new RTCPeerConnection(config);

  this.pc1.addEventListener('icecandidate', this.onIceCandidate_.bind(
    this,
    this.pc2,
  ));
  this.pc2.addEventListener('icecandidate', this.onIceCandidate_.bind(
    this,
    this.pc1,
  ));

  this.iceCandidateFilter_ = Call.noFilter;
}

Call.prototype = {
  establishConnection() {
    //this.traceEvent({state: 'start'});
    this.pc1.createOffer().then(
         this.gotOffer_.bind(this),
         this.test.reportFatal.bind(this.test),
    );
  },

  close() {
    //this.traceEvent({state: 'end'});
    this.pc1.close();
    this.pc2.close();
  },

  setIceCandidateFilter(filter) {
    this.iceCandidateFilter_ = filter;
  },

  // Constraint max video bitrate by modifying the SDP when creating an answer.
  constrainVideoBitrate(maxVideoBitrateKbps) {
    this.constrainVideoBitrateKbps_ = maxVideoBitrateKbps;
  },

  // Remove video FEC if available on the offer.
  disableVideoFec() {
    this.constrainOfferToRemoveVideoFec_ = true;
  },

  // When the peerConnection is closed the statsCb is called once with an array
  // of gathered stats.
  gatherStats(peerConnection, peerConnection2, localStream, statsCb) {
    const stats = [];
    const stats2 = [];
    const statsCollectTime = [];
    const statsCollectTime2 = [];
    const self = this;
    const statStepMs = 100;
    self.localTrackIds = {
      audio: '',
      video: '',
    };
    self.remoteTrackIds = {
      audio: '',
      video: '',
    };

    peerConnection.getSenders().forEach(((sender) => {
      if (sender.track.kind === 'audio') {
        self.localTrackIds.audio = sender.track.id;
      } else if (sender.track.kind === 'video') {
        self.localTrackIds.video = sender.track.id;
      }
    }));

    if (peerConnection2) {
      peerConnection2.getReceivers().forEach(((receiver) => {
        if (receiver.track.kind === 'audio') {
          self.remoteTrackIds.audio = receiver.track.id;
        } else if (receiver.track.kind === 'video') {
          self.remoteTrackIds.video = receiver.track.id;
        }
      }));
    }

    this.statsGatheringRunning = true;
    getStats_();

    function getStats_() {
      if (peerConnection.signalingState === 'closed') {
        self.statsGatheringRunning = false;
        statsCb(stats, statsCollectTime, stats2, statsCollectTime2);
        return;
      }
      peerConnection.getStats()
          .then(gotStats_)
          .catch(((error) => {
            self.test.reportError(`Could not gather stats: ${error}`);
            self.statsGatheringRunning = false;
            statsCb(stats, statsCollectTime);
          }));
      if (peerConnection2) {
        peerConnection2.getStats()
            .then(gotStats2_);
      }
    }
    // Stats for pc2, some stats are only available on the receiving end of a
    // peerconnection.
    function gotStats2_(response) {
      if (adapter.browserDetails.browser === 'chrome') {
        const enumeratedStats = util.enumerateStats(
          response, self.localTrackIds,
          self.remoteTrackIds,
        );
        stats2.push(enumeratedStats);
        statsCollectTime2.push(Date.now());
      } else if (adapter.browserDetails.browser === 'firefox') {
        const enumeratedStats = util.enumerateStatsFirefox(response);
        stats2.push(enumeratedStats);
        statsCollectTime2.push(Date.now());
      } else {
        self.test.reportError('Only Firefox and Chrome getStats ' +
            'implementations are supported.');
      }
    }

    function gotStats_(response) {
      // TODO: Remove browser specific stats gathering hack once adapter.js or
      // browsers converge on a standard.
      if (adapter.browserDetails.browser === 'chrome') {
        const enumeratedStats = util.enumerateStats(
          response, self.localTrackIds,
          self.remoteTrackIds,
        );
        stats.push(enumeratedStats);
        statsCollectTime.push(Date.now());
      } else if (adapter.browserDetails.browser === 'firefox') {
        const enumeratedStats = util.enumerateStatsFirefox(response);
        stats.push(enumeratedStats);
        statsCollectTime.push(Date.now());
      } else {
        self.test.reportError('Only Firefox and Chrome getStats ' + 'implementations are supported.');
      }
      setTimeout(getStats_, statStepMs);
    }
  },

  gotOffer_(offer) {
    if (this.constrainOfferToRemoveVideoFec_) {
      offer.sdp = offer.sdp.replace(
        /(m=video 1 [^\r]+)(116 117)(\r\n)/g,
        '$1\r\n',
      );
      offer.sdp = offer.sdp.replace(/a=rtpmap:116 red\/90000\r\n/g, '');
      offer.sdp = offer.sdp.replace(/a=rtpmap:117 ulpfec\/90000\r\n/g, '');
      offer.sdp = offer.sdp.replace(/a=rtpmap:98 rtx\/90000\r\n/g, '');
      offer.sdp = offer.sdp.replace(/a=fmtp:98 apt=116\r\n/g, '');
    }
    this.pc1.setLocalDescription(offer);
    this.pc2.setRemoteDescription(offer);
    this.pc2.createAnswer().then(
         this.gotAnswer_.bind(this),
         this.test.reportFatal.bind(this.test),
     );
  },

  gotAnswer_(answer) {
    if (this.constrainVideoBitrateKbps_) {
      answer.sdp = answer.sdp.replace(
        /a=mid:video\r\n/g,
        `a=mid:video\r\nb=AS:${this.constrainVideoBitrateKbps_}\r\n`,
      );
    }
    this.pc2.setLocalDescription(answer);
    this.pc1.setRemoteDescription(answer);
  },

  onIceCandidate_(otherPeer, event) {
    if (event.candidate) {
      const parsed = Call.parseCandidate(event.candidate.candidate);
      if (this.iceCandidateFilter_(parsed)) {
        otherPeer.addIceCandidate(event.candidate);
      }
    }
  },
};

Call.noFilter = function () {
  return true;
};

Call.isRelay = function (candidate) {
  return candidate.type === 'relay';
};

Call.isNotHostCandidate = function (candidate) {
  return candidate.type !== 'host';
};

Call.isReflexive = function (candidate) {
  return candidate.type === 'srflx';
};

Call.isHost = function (candidate) {
  return candidate.type === 'host';
};

Call.isIpv6 = function (candidate) {
  return candidate.address !== undefined && candidate.address.indexOf(':') !== -1;
};

// Parse a 'candidate:' line into a JSON object.
Call.parseCandidate = function (text) {
  const candidateStr = 'candidate:';
  const pos = text.indexOf(candidateStr) + candidateStr.length;
  const fields = text.substr(pos).split(' ');
  return {
    type: fields[7],
    protocol: fields[2],
    address: fields[4],
  };
};

// Store the ICE server response from the network traversal server.
Call.cachedIceServers_ = null;
// Keep track of when the request was made.
Call.cachedIceConfigFetchTime_ = null;

// Get a TURN config, either from settings or from network traversal server.
Call.asyncCreateTurnConfig = function (onSuccess, onError, region) {
  const settings = enxConfig.ice_servers[region];
  //console.log(`region: ${region} server: TURN`);
  if (settings.turnURI !== '') {
    const iceServer = {
      username: settings.turnUsername,
      credential: settings.turnCredential,
      urls: settings.turnURI,
    };
    const config = { iceServers: [iceServer] };
    //report.traceEventInstant('turn-config', config);
    setTimeout(onSuccess.bind(null, config), 0);
  }
 
};

// Get a STUN config, either from settings or from network traversal server.
Call.asyncCreateStunConfig = function (onSuccess, onError, region) {
  //console.log(`region: ${region} server: STUN`);
  const settings = enxConfig.ice_servers[region];
  if (settings.stunURI !== '') {
    const iceServer = {
      urls: settings.stunURI,
    };
    const config = { iceServers: [iceServer] };
    //report.traceEventInstant('stun-config', config);
    setTimeout(onSuccess.bind(null, config), 0);
  }
 
};


if (typeof exports === 'object') {
  module.exports = Call;
}



//////////////////
// WEBPACK FOOTER
// ./src/testWebRtc/call.js
// module id = 19
// module chunks = 0